import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Avatar,
  Rating,
} from "@mui/material";
import {
  FormatQuote,
  FiberManualRecord,
} from "@mui/icons-material";
import { styled, keyframes } from "@mui/material/styles";
import { motion } from "framer-motion";

const TESTIMONIALS = [
  {
    id: 1,
    name: "Dr. Ramesh",
    designation: "Subject matter specialist, Dept of Horticulture",
    content: "Highly useful for saving pesticides with the help of sensors, it avoids row gaps.",
    rating: 5,
    avatar: "/testimonials/ramesh.jpg",
  },
  {
    id: 2,
    name: "Farmer",
    designation: "Shergrah, Sirsa, Haryana",
    content: "No worries about diesel expense and can move through tight spaces and save me from harmful spray.",
    rating: 5,
    avatar: "/testimonials/f1.jpg",
  },
  {
    id: 3,
    name: "Dr. Devender Kumar Bishnoi",
    designation: "Govt senior medical officer, Hanumangarh",
    content: "Labors will now be safe from harmful chemicals while spraying pesticides.",
    rating: 5,
    avatar: "/testimonials/devender.jpg",
  },
  {
    id: 4,
    name: "Dr. Rinku",
    designation: "Subject matter specialist, Dept of Horticulture",
    content: "Solution against rising cancer risk due to pesticide exposure.",
    rating: 5,
    avatar: "/testimonials/rinku.jpg",
  },
  {
    id: 5,
    name: "Farmer",
    designation: "Abohar, fazilika, Punjab",
    content: "Big labor issue and automist500x can help with that. It also covers 360 degree of the crop from top to bottom.",
    rating: 5,
    avatar: "/testimonials/f4.png",
  },
  // {
  //   id: 6,
  //   name: "Farmer",
  //   designation: "Loharu, Jhajjar, Haryana",
  //   content: "We've deployed AUTOMIST500x across multiple client farms. The consistent performance, ease of maintenance, and impressive payload capacity have exceeded all expectations. It's the future of precision agriculture.",
  //   rating: 5,
  //   avatar: "/testimonials/f3.jpg",
  // },
];

// Animations
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const starPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
`;

// Styled Components
const BackgroundBox = styled(Box)(({ theme }) => ({
  position: "relative",
  background: "linear-gradient(180deg, #000000 0%, #0a0a0a 50%, #000000 100%)",
  overflow: "hidden",
  paddingTop: "40px",
  paddingBottom: "40px",
  [theme.breakpoints.down('md')]: {
    paddingTop: "30px",
    paddingBottom: "30px",
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: "24px",
    paddingBottom: "24px",
  },
}));

const GradientOrb = styled(Box)(({ top, left, bottom, right, color }) => ({
  position: "absolute",
  width: 500,
  height: 500,
  borderRadius: "50%",
  background: color,
  filter: "blur(100px)",
  pointerEvents: "none",
  top,
  left,
  bottom,
  right,
}));

const GridPattern = styled(Box)({
  position: "absolute",
  inset: 0,
  opacity: 0.05,
  backgroundImage: `linear-gradient(rgba(148, 163, 184, 0.1) 1px, transparent 1px),
                     linear-gradient(90deg, rgba(148, 163, 184, 0.1) 1px, transparent 1px)`,
  backgroundSize: "50px 50px",
  pointerEvents: "none",
});

const TestimonialCard = styled(Card)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.7)",
  backdropFilter: "blur(20px)",
  borderRadius: 20,
  [theme.breakpoints.down('sm')]: {
    borderRadius: 16,
  },
  border: "1px solid rgba(193, 227, 40, 0.3)",
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
  overflow: "hidden",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  transition: "all 0.3s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(193, 227, 40, 0.05) 0%, transparent 50%, rgba(193, 227, 40, 0.05) 100%)",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:hover": {
    borderColor: "rgba(193, 227, 40, 0.5)",
    transform: "translateY(-8px)",
    "&::before": {
      opacity: 1,
    },
  },
}));

const QuoteIcon = styled(FormatQuote)(({ theme }) => ({
  position: "absolute",
  top: 16,
  right: 16,
  fontSize: 48,
  [theme.breakpoints.down('sm')]: {
    fontSize: 36,
    top: 12,
    right: 12,
  },
  color: "#c1e328",
  opacity: 0.1,
  transition: "opacity 0.3s ease",
  ".MuiCard-root:hover &": {
    opacity: 0.2,
  },
}));

const StyledRating = styled(Rating)(({ theme }) => ({
  "& .MuiRating-iconFilled": {
    color: "#c1e328",
  },
  "& .MuiRating-icon": {
    animation: `${starPulse} 2s ease-in-out infinite`,
  },
  "& .MuiRating-icon:nth-of-type(1)": {
    animationDelay: "0s",
  },
  "& .MuiRating-icon:nth-of-type(2)": {
    animationDelay: "0.1s",
  },
  "& .MuiRating-icon:nth-of-type(3)": {
    animationDelay: "0.2s",
  },
  "& .MuiRating-icon:nth-of-type(4)": {
    animationDelay: "0.3s",
  },
  "& .MuiRating-icon:nth-of-type(5)": {
    animationDelay: "0.4s",
  },
}));

const GradientAvatar = styled(Avatar)(({ theme }) => ({
  width: 48,
  height: 48,
  [theme.breakpoints.down('sm')]: {
    width: 40,
    height: 40,
  },
  background: "linear-gradient(135deg, #c1e328 0%, #9fcc2e 100%)",
  border: "2px solid #c1e328",
  fontSize: "0.875rem",
  [theme.breakpoints.down('sm')]: {
    fontSize: "0.75rem",
  },
  fontWeight: 700,
  position: "relative",
  "& img": {
    objectFit: "cover",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #c1e328 0%, #9fcc2e 100%)",
    filter: "blur(12px)",
    opacity: 0.5,
    zIndex: -1,
    transition: "opacity 0.3s ease",
  },
  ".MuiCard-root:hover &::before": {
    opacity: 0.75,
  },
}));

const CornerAccent = styled(Box)({
  position: "absolute",
  bottom: 0,
  left: 0,
  width: 128,
  height: 128,
  background: "linear-gradient(to top right, rgba(193, 227, 40, 0.1) 0%, transparent 100%)",
  filter: "blur(40px)",
  pointerEvents: "none",
});

const TestimonialCardComponent = ({ testimonial }) => {
  return (
    <TestimonialCard elevation={0}>
      <QuoteIcon />
      <CornerAccent />

      <CardContent sx={{ p: 0, position: "relative", zIndex: 1, flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Rating */}
        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <StyledRating value={testimonial.rating} readOnly size="small" />
        </Box>

        {/* Content */}
        <Typography
          variant="body1"
          sx={{
            color: "rgba(203, 213, 225, 1)",
            lineHeight: 1.6,
            fontStyle: "italic",
            mb: { xs: 2, sm: 3 },
            fontSize: { xs: "0.8rem", sm: "0.9rem" },
            flex: 1,
          }}
        >
          "{testimonial.content}"
        </Typography>

        {/* Author Info */}
        <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="center" sx={{ mt: "auto" }}>
          <GradientAvatar src={testimonial.avatar} alt={testimonial.name} />
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ color: "#fff", fontWeight: 700, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
            >
              {testimonial.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#c1e328", fontWeight: 500, fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
            >
              {testimonial.designation}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </TestimonialCard>
  );
};

const HomepageTestimonials = () => {
  // Create a long array for seamless infinite scroll
  const infiniteTestimonials = [...TESTIMONIALS, ...TESTIMONIALS, ...TESTIMONIALS, ...TESTIMONIALS];

  return (
    <BackgroundBox>
      {/* Background Elements */}
      <GradientOrb top="33%" left={-256} color="rgba(193, 227, 40, 0.1)" />
      <GradientOrb bottom="33%" right={-256} color="rgba(193, 227, 40, 0.1)" />
      <GridPattern />

      <Container maxWidth="lg" sx={{ position: "relative", px: { xs: 2, sm: 3 } }}>
        {/* Section Header */}
        <Box textAlign="center" mb={{ xs: 4, sm: 6, md: 8 }}>
          {/* Badge */}
          <Chip
            icon={
              <FiberManualRecord
                sx={{
                  fontSize: 8,
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
              />
            }
            label="CLIENT STORIES"
            sx={{
              background: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(193, 227, 40, 0.3)",
              color: "#c1e328",
              fontWeight: 700,
              letterSpacing: { xs: 2, sm: 3.2 },
              fontSize: { xs: "0.5rem", sm: "0.555rem" },
              mb: { xs: 2, sm: 3 },
            }}
          />

          {/* Title */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
              fontWeight: 700,
              color: "#fff",
              letterSpacing: -1,
              mb: 2,
            }}
          >
            What Our {" "}
            <Box
              component="span"
              sx={{
                background:
                  "linear-gradient(135deg, #c1e328 0%, #b5d028 50%, #a8c728 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Clients Say
            </Box>
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="h6"
            sx={{
              color: "rgba(148, 163, 184, 1)",
              maxWidth: 700,
              mx: "auto",
              lineHeight: 1.8,
              fontSize: { xs: "0.875rem", md: "1rem" },
            }}
          >
            Real experiences from farmers and agricultural professionals who
            trust AUTOMIST500x
          </Typography>
        </Box>
      </Container>

      {/* Testimonial Carousel - Full Width - Continuous Scroll */}
      <Box sx={{ mb: { xs: 4, sm: 6 }, overflow: "hidden", position: "relative", width: "100%", paddingY: { xs: 1, sm: 2 } }}>
          <motion.div
            style={{
              display: "flex",
              gap: "24px",
              paddingLeft: "24px",
            }}
            animate={{
              x: [0, -100 * TESTIMONIALS.length + "%"],
            }}
            transition={{
              x: {
                repeat: Infinity,
                repeatType: "loop",
                duration: 150, // Adjust speed (higher = slower)
                ease: "linear",
              },
            }}
            whileHover={{
              animationPlayState: "paused",
            }}
          >
            {infiniteTestimonials.map((testimonial, index) => (
              <Box
                key={`${testimonial.id}-${index}`}
                sx={{
                  minWidth: { xs: "85vw", sm: "45vw", md: "30vw", lg: "25vw" },
                  maxWidth: { xs: "85vw", sm: "45vw", md: "30vw", lg: "25vw" },
                  flexShrink: 0,
                }}
              >
                <TestimonialCardComponent testimonial={testimonial} />
              </Box>
            ))}
          </motion.div>

          {/* Gradient Overlays for fade effect */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "100px",
              background: "linear-gradient(to right, #000000, transparent)",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: "100px",
              background: "linear-gradient(to left, #000000, transparent)",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
        </Box>

    
    </BackgroundBox>
  );
};

export default HomepageTestimonials;